import { formatMultiSelect } from "helpers/formatMultiSelect";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
} from "reactstrap";
import {
  DASHBOARD_DEFAULT_ORDER_TYPES,
  isDashboardAllOrderTypes,
  ORDER_TYPE_FILTER_OPTIONS,
} from "./orderManagementDashboardUtils";

function getSelectedOrderTypeList(selectedOrderTypes) {
  return Array.isArray(selectedOrderTypes) ? selectedOrderTypes : [];
}

/**
 * Multi-select order type filter for dashboard (values 1, 2, 3) + Tất cả.
 * Props: selectedOrderTypes (null = tất cả), onChange, label
 */
const DashboardOrderTypeMultiFilter = ({
  selectedOrderTypes = [],
  onChange,
  label = "Loại đơn",
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const isAllSelected = isDashboardAllOrderTypes(selectedOrderTypes);
  const selectedOrderTypeList = getSelectedOrderTypeList(selectedOrderTypes);

  const summaryLabel = useMemo(() => {
    if (isAllSelected) {
      return "Tất cả";
    }

    const labels = selectedOrderTypeList.map(value => {
      const option = ORDER_TYPE_FILTER_OPTIONS.find(item => item.value === value);

      return option?.label || value;
    });

    return formatMultiSelect(labels, "Chưa chọn");
  }, [isAllSelected, selectedOrderTypeList]);

  const handleSelectAll = () => {
    onChange(null);
  };

  const handleToggleOrderType = orderTypeValue => {
    if (isAllSelected) {
      onChange([orderTypeValue]);
      return;
    }

    const isChecked = selectedOrderTypeList.includes(orderTypeValue);
    let nextOrderTypes = isChecked
      ? selectedOrderTypeList.filter(item => item !== orderTypeValue)
      : [...selectedOrderTypeList, orderTypeValue];

    if (nextOrderTypes.length === 0) {
      nextOrderTypes = [...DASHBOARD_DEFAULT_ORDER_TYPES];
    }

    onChange(nextOrderTypes);
  };

  return (
    <Dropdown
      className="d-inline-block align-middle mw-100"
      isOpen={isOpen}
      toggle={() => setIsOpen(prev => !prev)}
    >
      <DropdownToggle
        className="btn primary-btn text-overflow d-inline-flex align-items-center"
        caret
        style={{
          width: "fit-content",
          maxWidth: "min(420px, calc(100vw - 24px))",
          whiteSpace: "nowrap",
        }}
      >
        {label}
        <span
          style={{
            display: "inline-block",
            maxWidth: "min(220px, 55vw)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            verticalAlign: "bottom",
          }}
        >
          :&nbsp;{summaryLabel}
        </span>
      </DropdownToggle>
      <DropdownMenu
        className="border shadow-sm w-100"
        style={{
          maxHeight: 280,
          overflowX: "hidden",
          overflowY: "auto",
          paddingTop: 6,
          paddingBottom: 6,
          scrollbarWidth: "thin",
        }}
      >
        <DropdownItem
          toggle={false}
          className="px-3 py-2 text-dark font-weight-medium"
          style={{ fontSize: "0.9375rem", cursor: "pointer" }}
          onClick={handleSelectAll}
        >
          Tất cả
        </DropdownItem>
        <DropdownItem divider />
        {ORDER_TYPE_FILTER_OPTIONS.map((item, index) => {
          const isChecked =
            !isAllSelected && selectedOrderTypeList.includes(item.value);

          return (
            <DropdownItem
              toggle={false}
              key={`order-type-multi-${item.value}-${index}`}
              className="d-flex align-items-center px-3 py-2 text-dark"
              style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              onClick={() => handleToggleOrderType(item.value)}
            >
              <Input
                type="checkbox"
                className="form-check-input position-static flex-shrink-0 m-0"
                style={{
                  width: "1.125rem",
                  height: "1.125rem",
                  pointerEvents: "none",
                }}
                checked={isChecked}
                readOnly
                tabIndex={-1}
              />
              <span className="ms-2">{item.label}</span>
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
};

DashboardOrderTypeMultiFilter.propTypes = {
  selectedOrderTypes: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.oneOf([null]),
  ]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};

export default DashboardOrderTypeMultiFilter;
